import Vue from 'vue';
import AjaxUtils from '../../util/ajax_utils';
import { objsAreEqual, removeEmptyVals } from '../_ui/_utils/useObjectUtils';
import { ApckTrustVulnerabilityDetails } from './trust_vulnerability_details.js';
import { ISO_3166_NAMES } from '../../util/iso3166_tools';
import { delay } from '../_ui/_utils/useUiUtils';
import { COLOR_TO_TYPE } from '../_ui/_utils/useValidationUtils';
import {
    ApckVulnerabilityImpactColumn,
    ApckVulnerabilityStatusColumn,
    ApckVulnerabilityDomainColumn,
    ApckTrustThirdpartyDetailColumn,
    ApckVulnerabilityTrustLevelColumn
} from './trust_columns';

const IMPACT_LEVELS = {
    critical: {
        level: '5',
        label: 'Critical',
        icon: 'fa-triangle-exclamation',
        class: 'severity-icon-critical',
        color: 'red',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['red']
    },
    high: {
        level: '4',
        label: 'High',
        icon: 'fa-chevrons-up',
        class: 'severity-icon-high',
        color: 'orange',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['orange']
    },
    medium: {
        level: '3',
        label: 'Medium',
        icon: 'fa-bug',
        class: 'severity-icon-medium',
        color: 'yellow',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['yellow']
    },
    low: {
        level: '2',
        label: 'Low',
        icon: 'fa-chevrons-down',
        class: 'severity-icon-low',
        color: 'green',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['green']
    },
    info: {
        level: '1',
        label: 'Info',
        icon: 'fa-circle-info',
        class: 'severity-icon-info',
        color: 'blue',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['blue']
    },
    investigate: {
        level: '0',
        label: 'Investigate',
        icon: 'fa-binoculars',
        class: 'severity-icon-investigate',
        color: 'light-slate',
        active: true,
        value: 0,
        iconType: COLOR_TO_TYPE['light-slate']
    },
    unknown: {
        label: 'Unknown',
        icon: 'fa-question-circle',
        color: 'light-slate',
        class: 'text-light-slate',
        iconType: COLOR_TO_TYPE['light-slate']
    }
};

// Impact level filter box component
const ImpactFilterBox = {
    props: {
        label: {
            type: String,
            required: true
        },
        count: {
            type: Number,
            default: 0
        },
        impactClass: {
            type: String,
            required: true
        },
        icon: {
            type: String,
            default: 'fa-binoculars'
        },
        active: {
            type: Boolean,
            default: true
        }
    },
    methods: {
        handleClick() {
            this.$emit('toggle');
        }
    },
    template: `
        <div 
            :class="['impact-filter-box', 'panel-box', impactClass, { 'active': active, 'inactive': !active }]"
            @click="handleClick"
        >
            <i :class="['fa', icon, 'impact-filter-icon']"></i>
            <div class="impact-filter-label">{{ label }}</div>
            <div class="impact-filter-count">{{ count }}</div>
        </div>
    `
};

export const ApckTrustVulnerabilitiesTable = {
    components: {
        'apck-trust-vulnerability-details': ApckTrustVulnerabilityDetails
    },
    props: {
        src: {
            type: String,
            default: '/trust/vulnerabilities/list'
        }
    },
    data() {
        return {
            loading: false,
            exportingHtml: false,
            exportingPdf: false,
            detailsModal: {
                render: false,
                open: false,
                id: null
            },
            filters: {
                domain: null,
                //this gets set as a string eg 5,4,3,2,1
                impact_levels: null,
                country: null,
                name: null,
                thirdparty: null,
                trust_level: null,
                status: null,
                date_from: null,
                date_to: null
            },
            impactLevels: null,
            availableFilters: {
                domains: [],
                trustLevels: [
                    { value: 5, label: 'Critical' },
                    { value: 4, label: 'High' },
                    { value: 3, label: 'Medium' },
                    { value: 2, label: 'Low' },
                    { value: 1, label: 'Informational' }
                ],
                statuses: [
                    { value: 'unfixed', label: 'Unfixed' },
                    { value: 'fixed', label: 'Fixed' },
                    { value: 'false_positive', label: 'False Positive' },
                    { value: 'acceptable_risk', label: 'Acceptable Risk' }
                ]
            },
            suggestions: {
                countries: [],
                thirdparty: [],
                names: []
            },
            cols: [
                {"data": "highest_vulnerability_impact", "heading": "Impact", "ctype": "vulnerability-impact", "width": 50, "center": true},
                {"data": "name", "heading": "Title"},
                {"data": "trust_domain", "heading": "Domain", "ctype": "vulnerability-domain", "width": 180},
                {"data": "thirdparty", "heading": "Third Party", "ctype": "trust-thirdparty-detail", "width": 300},
                {"data": "status", "heading": "Status", "ctype": "vulnerability-status", "width": 120}
            ],
            actions: [{
                title: 'View Details',
                icon: 'fa-eye',
                class: 'btn-secondary',
                handler: this.viewDetails,
                useOwnCol: true,
                hideLabel: true,
            }]
        };
    },
    computed: {
        tableSrc() {
            return this.src;
        },
        filterOptions() {
            return {
                domains: [
                    {
                        label: 'All Domains',
                        value: null,
                    },
                    ...this.availableFilters.domains.map((item) => ({
                        label: item.value,
                        value: item.value,
                    })),
                ],
                trustLevels: [
                    {
                        label: 'All Trust Levels',
                        value: null,
                    },
                    ...this.availableFilters.trustLevels.map((item) => ({
                        label: item.label,
                        value: String(item.value),
                    })),
                ],
                statuses: [
                    {
                        label: 'All Statuses',
                        value: null,
                    },
                    ...this.availableFilters.statuses.map((item) => ({
                        label: item.label,
                        value: item.value,
                    })),
                ],
            }
        },
        domainFilterLabel() {
            const selectedDomain = this.filters.domain;
            const matchingDomain = this.filterOptions.domains.find(domain => domain.value === selectedDomain);
            return matchingDomain ? matchingDomain.label : this.filterOptions.domains[0].label;
        },
        trustLevelFilterLabel() {
            const selectedLevel = this.filters.trust_level;
            const matchingLevel = this.filterOptions.trustLevels.find(level => level.value === selectedLevel);
            return matchingLevel ? matchingLevel.label : this.filterOptions.trustLevels[0].label;
        },
        statusFilterLabel() {
            const selectedStatus = this.filters.status;
            const matchingStatus = this.filterOptions.statuses.find(status => status.value === selectedStatus);
            return matchingStatus ? matchingStatus.label : this.filterOptions.statuses[0].label;
        },
        tableKey() {
            return `vulnerabilities-table-${this._uid}`
        },
        hasFilters() {
            return Object.keys(removeEmptyVals(this.filters)).length > 0;
        },
        hasRouteQuery() {
            return Object.keys(this.$route.query).length > 0
        },
        queriesMatch() {
            const routeQuery = this.$route.query;
            const activeFilters = removeEmptyVals(this.filters);
            const query = removeEmptyVals(routeQuery)

            return objsAreEqual(activeFilters, query)
        },
        disableClear() {
            return !this.hasFilters && !this.hasRouteQuery
        },
        disableSubmit() {
            return this.disableClear || this.queriesMatch
        },
        impactBoxes() {
            return Object.values(this.impactLevels || {});
        },
    },
    methods: {
        createInitialImpactLevels() {
            const currentImpactLevels = this.impactLevels;
            const isObject = currentImpactLevels ? Object.keys(currentImpactLevels).length : false;
            this.impactLevels = Object.fromEntries(
                Object.entries(IMPACT_LEVELS)
                    .filter(([key]) => key !== 'unknown')
                    .map(([key, config]) => [
                        key,
                        {
                            ...config,
                            disabled: !config.active,
                            value: isObject ? currentImpactLevels[key].value : config.value,
                            handler: () => this.toggleImpactFilter(key),
                        },
                    ]),
            );
        },
        async viewDetails(rec) {
            const vulnId = rec.get('vulnerability_id');
            if (!vulnId) {
                console.error('No vulnerability_id found in record:', rec);
                return;
            }
            
            this.detailsModal.id = vulnId;
            this.detailsModal.render = true;
            //wait for render before opening
            await delay(0);
            this.detailsModal.open = true;
        },
        async closeDetails() {
            this.detailsModal.open = false
            //wait for transition before removing
            await delay(400)
            this.detailsModal.render = false
            this.detailsModal.id = null
        },
        toggleImpactFilter(impactLevel) {
            this.impactLevels[impactLevel].active = !this.impactLevels[impactLevel].active;
            this.impactLevels[impactLevel].disabled = !this.impactLevels[impactLevel].disabled;

            // Get all active impact levels and sort by level number
            const activeImpacts = Object.entries(this.impactLevels)
                .filter(([_, config]) => config.active)
                .map(([_, config]) => config.level)
                .sort((a, b) => Number(b) - Number(a));

            this.filters.impact_levels = activeImpacts.join(',');

            this.handleSubmit();
        },
        setDomainFilter(value) {
            this.filters.domain = value || null
        },
        setCountryFilter(value) {
            this.filters.country = value ? value.toUpperCase() : null
        },
        setNameFilter(value) {
            this.filters.name = value || null
        },
        setThirdPartyFilter(value) {
            this.filters.thirdparty = value ? value.toLowerCase() : null
        },
        setTrustLevelFilter(value) {
            this.filters.trust_level = value || null
        },
        setStatusFilter(value) {
            this.filters.status = value !== undefined ? value : null
        },
        handleSubmit() {
            if(this.disableSubmit) return

            if(this.hasFilters) {
                this.applyFilters()
            } else {
                this.clearFilters()
            }
        },
        dropdownsChangedToAll() {
            return (
                (!this.filters.domain && this.$route.query.domain)
                || (!this.filters.trust_level && this.$route.query.trust_level)
                || (!this.filters.status && this.$route.query.status)
            )
        },
        clearFilters() {
            this.filters = {
                domain: null,
                impact_levels: null,
                country: null,
                name: null,
                thirdparty: null,
                trust_level: null,
                status: null,
                date_from: null,
                date_to: null
            };

            this.createInitialImpactLevels();

            this.$router.push({
                query: {}
            })

            this.applyFilters()
        },
        getFiltersFromUrl() {
            if(!this.hasRouteQuery) return;

            const originalFilters = this.filters
            const queryFilters = removeEmptyVals(this.$route.query);

            this.filters = {
                ...originalFilters,
                ...queryFilters,
            };

            const impactLevels = this.filters.impact_levels;
            if (impactLevels) {
                const impactLevelArray = impactLevels.split(',') || [];

                if (impactLevelArray?.length === 0) return;

                Object.entries(this.impactLevels).forEach(([key, config]) => {
                    if (!impactLevelArray.includes(config.level)) {
                        this.impactLevels[key].active = false;
                        this.impactLevels[key].disabled = true;
                    }
                });
            }
        },
        updateUrlQuery() {
            if (this.hasFilters && !this.queriesMatch) {

                const queryParams = removeEmptyVals(this.filters);

                this.$router.push({
                    query: queryParams,
                });
            }
        },
        applyFilters() {
            if (this.$refs.vulnerabilitiesTable) {

                this.updateUrlQuery()

                // Build query parameters from filters
                let params = {};
                Object.keys(this.filters).forEach(key => {
                    params[key] = this.filters[key];
                });

                // Update table with filters
                this.$refs.vulnerabilitiesTable.$data.store.load(params);
            }
        },
        async loadFilterOptions() {
            // Load available domains for filter dropdown
            const cmp = this;

            await AjaxUtils.ajax_get({
                url: '/trust/scope',
                success: (data) => {
                    if (data && data.success) {
                        cmp.availableFilters.domains = data.data || [];
                    }
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    show_success_flash: false,
                    flash_404_errors: false
                }
            });
        },
        fetchSuggestions() {
            AjaxUtils.ajax_get({
                url: this.tableSrc + '?limit=1000',
                success: (data) => {
                    if(data?.success) {
                        const items = data.items || []

                        this.suggestions.countries = Object.entries(ISO_3166_NAMES)
                            .map(([key, value]) => {
                                return {
                                    label: `${key} - ${value}`,
                                    flag: key,
                                    value: key
                                }
                            });

                        this.suggestions.thirdparty = [...new Set(
                            items
                                .map(item => item.thirdparty?.hostname)
                                .filter(thirdparty => thirdparty)
                        )];

                        this.suggestions.names = [...new Set(
                            items
                                .map(item => item.name)
                                .filter(name => name)
                        )];
                    }
                },
                error: (data) => {
                    console.error('Failed to fetch suggestions:', data)
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    show_success_flash: false,
                    flash_404_errors: false
                }
            });
        },
        updateSummary(summary) {
            Object.entries(summary).forEach(([key, count]) => {
                if (key in this.impactLevels) {
                    this.impactLevels[key].value = count;
                }
            });
        },
        async loadSummary() {
            const cmp = this;

            await AjaxUtils.ajax_get({
                url: this.tableSrc + '?limit=1',
                success: (data) => {
                    if (data && data.success && data.summary) {
                        cmp.updateSummary(data.summary);
                    }
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    show_success_flash: false,
                    flash_404_errors: false
                }
            });
        },
        exportReport(format) {
            let cmp = this;
            
            // Set loading state
            if (format === 'pdf') {
                cmp.exportingPdf = true;
            } else {
                cmp.exportingHtml = true;
            }
            
            // Build query parameters from current filters
            const params = new URLSearchParams();
            
            // Add domain filter
            if (this.filters.domain && this.filters.domain !== '*') {
                params.append('domain_id', this.filters.domain);
            }
            
            // Add impact levels filter
            const activeImpacts = Object.entries(this.impactLevels)
                .filter(([key, data]) => data.active && data.level)
                .map(([key, data]) => data.level);
            
            if (activeImpacts.length > 0 && activeImpacts.length < 6) {
                params.append('impact_levels', activeImpacts.join(','));
            }
            
            // Add status filter
            if (this.filters.status && this.filters.status !== '*') {
                params.append('statuses', this.filters.status);
            }
            
            // Add name filter
            if (this.filters.name) {
                params.append('name', this.filters.name);
            }
            
            // Add thirdparty filter
            if (this.filters.thirdparty) {
                params.append('thirdparty', this.filters.thirdparty);
            }
            
            // Add country filter
            if (this.filters.country) {
                params.append('country', this.filters.country);
            }
            
            // Build URL based on format
            let url;
            if (format === 'pdf') {
                url = '/trust/reporting/vulnerabilities/pdf';
            } else {
                url = '/trust/reporting/vulnerabilities';
            }
            
            // Add query parameters
            if (params.toString()) {
                url += '?' + params.toString();
            }
            
            // Use fetch API for better download handling
            fetch(url, {
                credentials: 'same-origin' // Ensure cookies are sent
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                // Get the filename from the Content-Disposition header if available
                let filename = format === 'pdf' ? 'vulnerability-report.pdf' : 'vulnerability-report.html';
                const contentDisposition = response.headers.get('Content-Disposition');
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        // Remove quotes if present
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }

                return response.blob().then(blob => ({ blob, filename }));
            })
            .then(({ blob, filename }) => {
                // Create a URL for the blob and trigger download
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = filename; // Use the server-provided filename
                document.body.appendChild(link);
                link.click();

                // Clean up
                window.URL.revokeObjectURL(downloadUrl);
                document.body.removeChild(link);

                // Clear loading state
                if (format === 'pdf') {
                    cmp.exportingPdf = false;
                } else {
                    cmp.exportingHtml = false;
                }
            })
            .catch(error => {
                console.error('Error downloading vulnerability report:', error);
                
                // Clear loading state
                if (format === 'pdf') {
                    cmp.exportingPdf = false;
                } else {
                    cmp.exportingHtml = false;
                }
                
                Store.Flash.error("An error occurred while downloading the report.");

                // Fallback to direct URL navigation
                console.warn('Falling back to direct URL navigation');
                window.open(url, '_blank');
            });
        }
    },
    created() {
        // Register custom column components
        Vue.component('apck-vulnerability-impact', ApckVulnerabilityImpactColumn);
        Vue.component('apck-vulnerability-status', ApckVulnerabilityStatusColumn);
        Vue.component('apck-vulnerability-domain', ApckVulnerabilityDomainColumn);
        Vue.component('apck-trust-thirdparty-detail', ApckTrustThirdpartyDetailColumn);
        Vue.component('apck-vulnerability-trust-level', ApckVulnerabilityTrustLevelColumn);
        Vue.component('impact-filter-box', ImpactFilterBox);
    },
    async mounted() {
        this.createInitialImpactLevels()
        await this.loadFilterOptions();
        await this.loadSummary();
        this.getFiltersFromUrl()
        this.applyFilters();
        this.fetchSuggestions();
    },
    template: `
        <ui-page title="Vulnerability Reporting" stickyTitle>
            <template #title-bar-buttons>
                <ui-button
                    label="Interactive Report"
                    iconLeft="fa-file-code"
                    type="outline"
                    :disabled="exportingHtml || exportingPdf"
                    @click="exportReport('html')"
                >
                    <template v-if="exportingHtml">Generating...</template>
                </ui-button>
                <!--
                <ui-button
                    label="Vulnerability Report"
                    iconLeft="fa-file-pdf"
                    type="outline"
                    :disabled="exportingHtml || exportingPdf"
                    @click="exportReport('pdf')"
                >
                    <template v-if="exportingPdf">Generating...</template>
                </ui-button>
                -->
            </template>
            
            <template #content>
                <ui-layout direction="column">
                    <!-- Impact Level Filter Boxes -->
                    <ui-panel-group-data-list 
                        :data="impactBoxes"
                        layout="grid"
                        cols="6"
                        gap="4"
                        :loading="loading"
                        counter
                    />

                    <!-- Search and Filter Options -->
                    <ui-layout direction="column" justify="start">
                        <ui-layout gap="2" align="center">
                            <ui-icon type="secondary" icon="fa-filter" />
                            <ui-heading tag="span" size="md">Filters</ui-heading>
                        </ui-layout>
                        
                        <ui-layout
                            tag="form"
                            gap="4"
                            align="end"
                            name="trust_vulnerability_filter"
                            @keydown.enter.native.prevent="handleSubmit"
                            @submit.native.prevent="handleSubmit"
                        >
                            <ui-input-text
                                label="Vulnerability Title"
                                placeholder="Search by title"
                                type="text"
                                query="name"
                                :modelValue="filters.name"
                                :suggestions="suggestions.names"
                                :suggestionLimit="3"
                                @update:modelValue="setNameFilter"
                            />

                            <ui-dropdown-radio
                                name="domainFilter"
                                :modelValue="filters.domain"
                                :options="filterOptions.domains"
                                :label="domainFilterLabel"
                                align="left"
                                @update:modelValue="setDomainFilter"
                            />

                            <ui-input-text
                                label="Third Party"
                                placeholder="Enter third party domain"
                                type="text"
                                query="thirdparty"
                                :modelValue="filters.thirdparty"
                                :suggestions="suggestions.thirdparty"
                                :suggestionLimit="2"
                                @update:modelValue="setThirdPartyFilter"
                            />

                            <ui-dropdown-radio
                                name="trustLevelFilter"
                                :modelValue="filters.trust_level"
                                :options="filterOptions.trustLevels"
                                :label="trustLevelFilterLabel"
                                align="left"
                                @update:modelValue="setTrustLevelFilter"
                            />

                            <ui-dropdown-radio
                                name="statusFilter"
                                :modelValue="filters.status"
                                :options="filterOptions.statuses"
                                :label="statusFilterLabel"
                                align="left"
                                @update:modelValue="setStatusFilter"
                            />

                            <ui-input-text
                                label="Country"
                                placeholder="eg: United Kingdom"
                                type="text"
                                query="country"
                                :modelValue="filters.country"
                                :suggestions="suggestions.countries"
                                :suggestionLimit="1"
                                @update:modelValue="setCountryFilter"
                            />

                            <ui-button
                                label="Submit"
                                type="primary"
                                :disabled="disableSubmit"
                                nativeType="submit"
                            />

                            <ui-button
                                label="Clear"
                                type="primary"
                                :color="disableClear ? null : 'red'"
                                :disabled="disableClear"
                                @click.prevent="clearFilters"
                            />
                        </ui-layout>
                    </ui-layout>
                    
                    <!-- Vulnerabilities Table -->
                    <apck-table
                        ref="vulnerabilitiesTable"
                        class="trust-vulnerabilities-table"
                        :columns="cols"
                        :src="tableSrc"
                        :actions="actions"
                        :limit="100"
                        :border="false"
                        :autoload="false"
                        :key="tableKey"
                        :show-toolbar="false"
                        :sglRowAction="viewDetails"
                        newStyleTable
                    />
                </ui-layout>

                <ui-modal
                    v-if="detailsModal.render"
                    title="Vulnerability Details"
                    size="lg"
                    align="center"
                    justify="end"
                    fullHeight
                    :open.sync="detailsModal.open"
                    @close="closeDetails()"
                >
                    <template #content>
                        <apck-trust-vulnerability-details
                            :vulnerabilityId="detailsModal.id"
                            @close="closeDetails()"
                        />
                    </template>
                    <template #footer>
                        <ui-layout gap="2" justify="flex-end">
                            <ui-button
                                label="Close"
                                type="primary"
                                color="red"
                                @click="closeDetails"
                            />
                        </ui-layout>
                    </template>
                </ui-modal>
                
                <ui-spinner :show="loading || exportingHtml || exportingPdf" theme="dark" />
            </template>
        </ui-page>
    `
}
